import {
  Box,
  createStyles,
  Divider,
  Grid,
  makeStyles,
  Menu,
  MenuItem,
  Typography,
} from '@material-ui/core'
import MenuIcon from '@mui/icons-material/Menu'
import React, { useState } from 'react'
import {
  DateField,
  EditButton,
  FunctionField,
  ReferenceField,
  sanitizeFetchType,
  Show,
  ShowProps,
  Tab,
  TabbedShowLayout,
  TextField,
  TitleProps,
  useMutation,
  useNotify,
  useQuery,
  useRefresh,
  useShowContext,
  useTranslate,
} from 'react-admin'
import { Authority } from '../../../../core/auth/Authority'
import { ResourceName } from '../../../../core/ResourceName'
import { useHasAuthority } from '../../../hooks/useHasAuthority'
import { DeviceCategoryExtendedDto } from '../../../../core/dto/device-category/device-category-extended.dto'
import { CameraDto } from '../../../../core/dto/device/camera/cameras/camera.dto'
import { DeviceStatusesTab } from '../../common/modules/ModuleStatus'
import { DeviceTypes } from '../../../../core/enum/DeviceTypes'
import { IksorisParkingTicketCashlessMachineDto } from '../../../../core/dto/device/ticket-machines/iksoris-parking-ticket-cashless-machine/iksoris-parking-ticket-cashless-machine.dto'
import { ConfirmDialog } from '../../../common/ConfirmDialog'
import { SetTicketMachineAPIKeyDialog } from '../iksoris-parking-ticket-cash-machines/IksorisParkingTicketCashMachineDialogs'
import Button from '../../../common/customized-mui-components/Button'
import { BlankActions } from '../../../common/BlankActions'

const useStyles = makeStyles(() =>
  createStyles({
    machineActions: {
      right: '25px',
      position: 'absolute',
      whiteSpace: 'nowrap',
    },
  }),
)

interface IksorisParkingTicketCashlessMachineTitleProps extends TitleProps {
  record?: IksorisParkingTicketCashlessMachineDto
}

const ShowTitle = (data: IksorisParkingTicketCashlessMachineTitleProps) => {
  const { record: terminal } = data
  const translate = useTranslate()
  return (
    <span>
      {translate(
        'resources.iksoris-parking-ticket-cashless-machines.titles.show',
      )}
      : {terminal?.id}. {terminal?.name}
    </span>
  )
}

const GeneralActions = () => {
  const { record: ticketMachine } =
    useShowContext<IksorisParkingTicketCashlessMachineDto>()
  const hasAuthority = useHasAuthority()

  return (
    <>
      <Divider />
      <Box p={1}>
        <Grid container direction="row" justify="flex-end" alignItems="center">
          <Grid item>
            <EditButton
              basePath={`/${ResourceName.IKSORIS_PARKING_TICKET_CASHLESS_MACHINES}`}
              record={ticketMachine}
              variant="contained"
              style={{ margin: '2px' }}
              disabled={
                !hasAuthority(
                  Authority.EDIT_IKSORIS_PARKING_TICKET_CASHLESS_MACHINES,
                )
              }
            />
          </Grid>
        </Grid>
      </Box>
    </>
  )
}

export const IksorisParkingTicketCashlessMachineTabs = ({ ...props }) => {
  const { simpleView, createdAt, ...rest } = props
  const { record: ticketMachine } = rest
  const translate = useTranslate()
  const hasAuthority = useHasAuthority()
  const [mutate] = useMutation()
  const classes = useStyles()
  const [anchorEl, setAnchorEl] = React.useState(null)
  const [apiKeyModalOpen, setAPIKeyModalOpen] = useState(false)
  const [removeApiKeyModalOpen, setRemoveAPIKeyModalOpen] = useState(false)
  const refresh = useRefresh()
  const notify = useNotify()

  const { data: serverDate } = useQuery(
    {
      type: sanitizeFetchType('getServerDateTime'),
      resource: ResourceName.CURRENT_USER,
      payload: {},
    },
    {
      enabled: !simpleView && !createdAt,
    },
  )

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleRequest = async (id: number, fetchType: string) => {
    await mutate(
      {
        type: sanitizeFetchType(fetchType),
        resource: ResourceName.IKSORIS_PARKING_TICKET_CASHLESS_MACHINES,
        payload: { ticketMachineId: id },
      },
      {
        returnPromise: true,
        onFailure: (err) => notify(err?.message, 'error'),
      },
    )
    refresh()
  }

  const handleSetAPIKeyDialog = () => {
    setAPIKeyModalOpen(true)
  }

  const handleRemoveAPIKeyDialog = () => {
    setRemoveAPIKeyModalOpen(true)
  }

  return (
    <TabbedShowLayout {...rest} syncWithLocation={!simpleView}>
      <Tab label="resources.passages.tabs.general">
        <div className={classes.machineActions}>
          {hasAuthority(
            Authority.IKSORIS_PARKING_TICKET_CASHLESS_MACHINES_ACTIONS_LIST,
          ) && (
            <Button
              onClick={handleClick}
              label="const.selectAction"
              variant="contained"
            >
              <MenuIcon />
            </Button>
          )}
          <Menu
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={handleClose}
            onClick={handleClose}
          >
            {hasAuthority(
              Authority.IKSORIS_PARKING_TICKET_CASHLESS_MACHINES_LOCK_BUTTON,
            ) && (
              <MenuItem
                {...props}
                onClick={() => handleRequest(props?.record?.id, 'lock')}
              >
                <Typography>
                  {translate(
                    'resources.selfpos-access-ticket-cashless-machines.actions.lock',
                  )}
                </Typography>
              </MenuItem>
            )}
            {hasAuthority(
              Authority.IKSORIS_PARKING_TICKET_CASHLESS_MACHINES_UNLOCK_BUTTON,
            ) && (
              <MenuItem
                {...props}
                onClick={() => handleRequest(props?.record?.id, 'unlock')}
              >
                <Typography>
                  {translate(
                    'resources.selfpos-access-ticket-cashless-machines.actions.unlock',
                  )}
                </Typography>
              </MenuItem>
            )}
            {hasAuthority(
              Authority.IKSORIS_PARKING_TICKET_CASHLESS_MACHINES_RESTART_BUTTON,
            ) && (
              <MenuItem
                {...props}
                onClick={() => handleRequest(props?.record?.id, 'restart')}
              >
                <Typography>
                  {translate(
                    'resources.selfpos-access-ticket-cashless-machines.actions.restart',
                  )}
                </Typography>
              </MenuItem>
            )}
            {hasAuthority(
              Authority.IKSORIS_PARKING_TICKET_CASHLESS_MACHINES_REBOOT_DEVICE_BUTTON,
            ) && (
              <MenuItem
                {...props}
                onClick={() => handleRequest(props?.record?.id, 'rebootDevice')}
              >
                <Typography>
                  {translate(
                    'resources.selfpos-access-ticket-cashless-machines.actions.reboot-device',
                  )}
                </Typography>
              </MenuItem>
            )}
            {hasAuthority(
              Authority.IKSORIS_PARKING_TICKET_CASHLESS_MACHINES_SHUTDOWN_DEVICE_BUTTON,
            ) && (
              <MenuItem
                {...props}
                onClick={() =>
                  handleRequest(props?.record?.id, 'shutDownDevice')
                }
              >
                <Typography>
                  {translate(
                    'resources.selfpos-access-ticket-cashless-machines.actions.shutdown-device',
                  )}
                </Typography>
              </MenuItem>
            )}
            {hasAuthority(
              Authority.IKSORIS_PARKING_TICKET_CASHLESS_MACHINES_MANAGE_CREDENTIALS_BUTTON,
            ) && (
              <MenuItem {...props} onClick={handleSetAPIKeyDialog}>
                <Typography>
                  {translate('resources.ticket-machines.actions.setAPIKey')}
                </Typography>
              </MenuItem>
            )}
            <SetTicketMachineAPIKeyDialog
              open={apiKeyModalOpen}
              close={() => setAPIKeyModalOpen(false)}
              resource={ResourceName.IKSORIS_PARKING_TICKET_CASHLESS_MACHINES}
            />
            {hasAuthority(
              Authority.IKSORIS_PARKING_TICKET_CASHLESS_MACHINES_MANAGE_CREDENTIALS_BUTTON,
            ) && (
              <MenuItem {...props} onClick={handleRemoveAPIKeyDialog}>
                <Typography>
                  {translate('resources.ticket-machines.actions.removeAPIKey')}
                </Typography>
              </MenuItem>
            )}
            <ConfirmDialog
              open={removeApiKeyModalOpen}
              confirm={() => {
                handleRequest(props?.record?.id, 'removeAPIKey')
                setRemoveAPIKeyModalOpen(false)
              }}
              source="apiKey"
              cancel={() => setRemoveAPIKeyModalOpen(false)}
              action="remove"
              resource={ResourceName.IKSORIS_PARKING_TICKET_CASHLESS_MACHINES}
            />
          </Menu>
        </div>
        <TextField source="id" label="ID" />
        <TextField source="name" />
        <FunctionField<IksorisParkingTicketCashlessMachineDto>
          source="isWatched"
          render={(record?: IksorisParkingTicketCashlessMachineDto) => (
            <div>
              {record?.isWatched
                ? translate('const.yes')
                : translate('const.no')}
            </div>
          )}
        />
        <DateField
          source="lastRefresh"
          showTime
          options={{
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          }}
        />
        {ticketMachine?.categoryId &&
          hasAuthority(Authority.VIEW_DEVICE_CATEGORIES) && (
            <ReferenceField
              source="categoryId"
              reference={ResourceName.DEVICE_CATEGORIES}
              link="show"
            >
              <FunctionField<DeviceCategoryExtendedDto>
                source="hierarchyString"
                render={(record?: DeviceCategoryExtendedDto) =>
                  `${record?.id}. ${record?.hierarchyString}`
                }
              />
            </ReferenceField>
          )}
        {ticketMachine?.cameraId && hasAuthority(Authority.VIEW_CAMERAS) && (
          <ReferenceField
            source="cameraId"
            reference={ResourceName.CAMERAS}
            link="show"
          >
            <FunctionField<CameraDto>
              source="name"
              render={(camera?: CameraDto) =>
                `${camera?.id}. ${camera?.description}`
              }
            />
          </ReferenceField>
        )}
        <TextField source="address" />
        {(!simpleView && <GeneralActions />) || <BlankActions />}
      </Tab>
      <Tab label="resources.passages.tabs.statuses" path="statuses">
        {ticketMachine?.jsonStatus && (
          <DeviceStatusesTab
            jsonStatus={ticketMachine.jsonStatus}
            deviceType={DeviceTypes.IKSORIS_PARKING_TICKET_CASHLESS_MACHINE}
            referenceDateTime={createdAt || serverDate?.serverDateTime}
          />
        )}
      </Tab>
    </TabbedShowLayout>
  )
}

export const IksorisParkingTicketCashlessMachineShow = (props: ShowProps) => (
  <Show {...props} actions={false} title={<ShowTitle />}>
    <IksorisParkingTicketCashlessMachineTabs />
  </Show>
)
